import { Box, Button, Container, Typography, TextField, Grid, Paper, InputAdornment } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DealerList as FindDealerList, DealersMap as FindDealerMap } from '@/components/FindDealers/FindDealersIndex.ts';
import { ISearchDealerResult } from '@/types/ISearchDealerResult';
import { searchDealers } from '@/api/searchDealers';
import './FindDealers.css';
import SearchIcon from '@mui/icons-material/Search';

const FindDealer = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [dealers, setDealers] = useState<ISearchDealerResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [currentSearch, setCurrentSearch] = useState('');
  const [currentCoordinates, setCurrentCoordinates] = useState<{ latitude: number; longitude: number } | null>(null);
  const [currentSortBy, setCurrentSortBy] = useState('distance');
  const [selectedDealerId, setSelectedDealerId] = useState<number | null>(null);
  const hasInitialSearchRef = useRef(false);
  const isLocationRequested = useRef(false);

  const googleMapApiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;

  // Request location permission and get user coordinates on page load
  useEffect(() => {
    if (isLocationRequested.current) return; // prevent multiple requests

    const requestLocationPermission = async () => {
      if ('geolocation' in navigator) {
        isLocationRequested.current = true; // Mark as requested
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });

            // search dealers from user's location coordinates
            if (!hasInitialSearchRef.current) {
              try {
                setIsLoading(true);
                const searchResults = await searchDealers('', { latitude, longitude }, 'distance');
                if (searchResults) {
                  setDealers(searchResults);
                  setCurrentCoordinates({ latitude, longitude });
                  setSelectedDealerId(null);
                  hasInitialSearchRef.current = true;
                  console.log('Initial location-based API call completed');
                }
              } catch (error) {
                console.error('Error searching installers:', error);
              } finally {
                setIsLoading(false);
              }
            }
          },
          async (error) => {
             await defaultLocation(setIsLoading, setDealers, setCurrentCoordinates, setSelectedDealerId, hasInitialSearchRef);
            console.log('Location permission denied or error:', error.message);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000
          }
        );
      } else {
         await defaultLocation(setIsLoading, setDealers, setCurrentCoordinates, setSelectedDealerId, hasInitialSearchRef);
        console.log('Geolocation is not supported by this browser');
      }
    };

    requestLocationPermission();
  }, []);
  return (
    <Container className="find-dealers-page-container" maxWidth="lg">
      <Paper elevation={3} className="find-dealers-page">
        <Grid container spacing={2} className="panel-wrapper">
          <Grid item xs={12} md={9} className="left-panel-wrapper">
            <Box
              className={`find-dealers-maps-container ${
                googleMapApiKey ? "with-api-key" : "no-api-key"
              }`}
            >
              <FindDealerMap
                apiKey={googleMapApiKey}
                dealers={dealers}
                userLocation={userLocation}
                selectedDealerId={selectedDealerId}
              />
            </Box>
            <Box className="become-installer-box">
              <Typography
                className='become-installer-text'
                variant="h6"
              >
                Become a Trackmate Installer
              </Typography>
              <Box className="join-network-text">
                <Typography>Join our network of proffesionals and grow your business</Typography>
              </Box>
              <Button className="apply-btn" variant="contained"
                onClick={() => navigate('/resources/become-installer')}>
                Apply Here
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3} className='right-panel-wrapper'>
            <Box className="top-box">
              <Typography variant="h6" className="find-installer-text">
                Find Certified Installers in your area
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter your city, zip code or address"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === 'Enter') {
                    setIsLoading(true);
                    try {
                      let searchResults;
                      const sortBy = currentSortBy || 'distance';
                      if (search.trim()) {
                        searchResults = await searchDealers(search.trim(), undefined, sortBy);
                        if (searchResults) {
                          setDealers(searchResults);
                          setCurrentSearch(search.trim());
                          setCurrentCoordinates(null);
                          setSelectedDealerId(null);
                        }
                      } else if (userLocation) {
                        searchResults = await searchDealers('', { latitude: userLocation.lat, longitude: userLocation.lng }, sortBy);
                        if (searchResults) {
                          setDealers(searchResults);
                          setCurrentSearch('');
                          setCurrentCoordinates({ latitude: userLocation.lat, longitude: userLocation.lng });
                          setSelectedDealerId(null);
                        }
                      }
                    } catch (error) {
                      console.error('Error searching installers:', error);
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
                size="small"
                className="search-textbox"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ mt: 0.2, color: '#C4C4C4', fontSize: 24 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box className="bottom-box">
              <FindDealerList
                dealers={dealers}
                isLoading={isLoading}
                currentSearch={currentSearch}
                currentCoordinates={currentCoordinates}
                onDealerClick={(dealerId) => setSelectedDealerId(dealerId)}
                onSortChange={async (sortBy: string) => {
                  const sortByValue = sortBy || 'distance';
                  setCurrentSortBy(sortByValue);
                  setIsLoading(true);
                  try {
                    let searchResults;
                    if (currentSearch) {
                      searchResults = await searchDealers(currentSearch, undefined, sortByValue);
                    } else if (currentCoordinates) {
                      searchResults = await searchDealers('', currentCoordinates, sortByValue);
                    }
                    if (searchResults) {
                      setDealers(searchResults);
                      setSelectedDealerId(null);
                    }
                  } catch (error) {
                    console.error('Error searching installers with sort:', error);
                  } finally {
                    setIsLoading(false);
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};


async function defaultLocation(setIsLoading : any, setDealers: any, setCurrentCoordinates: any, setSelectedDealerId: any, hasInitialSearchRef: any) {
  try {
    setIsLoading(true);
    setCurrentCoordinates({ latitude: 40.7127281, longitude: -74.0060152 });
    const searchResults = await searchDealers('', { latitude: 40.7127281, longitude: -74.0060152 }, 'distance');
    if (searchResults) {
      setDealers(searchResults);
      setSelectedDealerId(null);
      hasInitialSearchRef.current = true;
      console.log('Initial location-based API call completed');
    }
  } catch (error) {
    console.error('Error searching installers:', error);
  } finally {
    setIsLoading(false);
  }
}


export default FindDealer; 
